import React from 'react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Fuel, 
  Truck, 
  Warehouse, 
  Settings, 
  Wrench, 
  Shield,
  CheckCircle2,
  Droplet,
  Zap
} from 'lucide-react';
import { products, services } from '../mock';

export const Products = () => {
  const serviceIcons = {
    'Wholesale Fuel Supply': Fuel,
    'Transportation & Logistics': Truck,
    'Fuel Storage Solutions': Warehouse,
    'Storage Facility Setup': Settings,
    'Maintenance Services': Wrench,
    'Compliance Consulting': Shield
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-50 via-teal-50 to-green-50 pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="mb-4 bg-emerald-600 text-white text-base px-4 py-1">Products & Services</Badge>
          <h1 className="text-3xl font-bold text-gray-900 mb-6">Comprehensive Petroleum Solutions</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Premium fuel products and expert services to power your operations
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="products" className="w-full">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
              <TabsTrigger value="products">Petroleum Products</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
            </TabsList>

            {/* Products Tab */}
            <TabsContent value="products" className="space-y-12">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Premium Petroleum Products</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  High-quality, uncontaminated fuel loaded directly from Shell, Engen & BP
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {products.map((product) => (
                  <Card key={product.id} className="border-2 border-gray-200 hover:border-emerald-300 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 group">
                    <CardHeader>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-600 transition-colors duration-300">
                          <Droplet className="h-6 w-6 text-emerald-600 group-hover:text-white transition-colors duration-300" />
                        </div>
                        <Badge variant="secondary" className="bg-teal-100 text-teal-700">
                          {product.category}
                        </Badge>
                      </div>
                      <CardTitle className="text-2xl">{product.name}</CardTitle>
                      <CardDescription className="text-gray-600 text-base">
                        {product.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Applications:</p>
                        {product.applications.map((app, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                            <span className="text-sm text-gray-600">{app}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Quality Assurance */}
              <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200">
                <CardHeader>
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-lg bg-emerald-600 flex items-center justify-center">
                      <Shield className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-2xl">Quality Assurance</CardTitle>
                      <CardDescription className="text-gray-700 text-base">
                        Every drop meets the highest industry standards
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold text-gray-900">Direct Loading</p>
                        <p className="text-sm text-gray-600">From Shell, Engen & BP facilities</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold text-gray-900">Uncontaminated</p>
                        <p className="text-sm text-gray-600">Rigorous quality control processes</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-semibold text-gray-900">Certified</p>
                        <p className="text-sm text-gray-600">Meets all regulatory standards</p>
                      </div>
                    </div>
                  </div>

                  {/* Laboratory Testing Partners */}
                  <div className="mt-8 pt-8 border-t border-emerald-200">
                    <div className="text-center mb-6">
                      <p className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-2">Laboratory Testing Partners</p>
                      <p className="text-gray-600">All our fuel quality is independently verified by industry-leading testing laboratories</p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {[
                        { name: 'SGS', desc: 'Global inspection & certification' },
                        { name: 'WearCheck', desc: 'Fuel & lubricant analysis' },
                        { name: 'Intertek', desc: 'Quality assurance leader' },
                        { name: 'Mobilab', desc: 'Mobile laboratory testing' }
                      ].map((lab, idx) => (
                        <div key={idx} className="bg-white border-2 border-emerald-200 rounded-lg p-4 text-center hover:border-emerald-400 hover:shadow-md transition-all duration-300">
                          <div className="text-2xl font-bold text-emerald-700 mb-1">{lab.name}</div>
                          <p className="text-xs text-gray-600">{lab.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Services Tab */}
            <TabsContent value="services" className="space-y-12">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Expert Services</h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Comprehensive support from supply to storage infrastructure
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                {services.map((service) => {
                  const Icon = serviceIcons[service.title] || Fuel;
                  return (
                    <Card key={service.id} className="border-2 border-gray-200 hover:border-teal-300 hover:shadow-xl transition-all duration-300 group">
                      <CardHeader>
                        <div className="flex items-start gap-4">
                          <div className="h-14 w-14 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0 group-hover:bg-teal-600 transition-colors duration-300">
                            <Icon className="h-7 w-7 text-teal-600 group-hover:text-white transition-colors duration-300" />
                          </div>
                          <div className="flex-1">
                            <CardTitle className="text-xl mb-2">{service.title}</CardTitle>
                            <CardDescription className="text-gray-600 text-base">
                              {service.description}
                            </CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                    </Card>
                  );
                })}
              </div>

              {/* Service Highlights */}
              <div className="grid md:grid-cols-3 gap-6 mt-12">
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardHeader>
                    <Zap className="h-10 w-10 text-emerald-600 mb-3" />
                    <CardTitle>Fast Response</CardTitle>
                    <CardDescription className="text-gray-700">
                      Quick turnaround on orders and service requests
                    </CardDescription>
                  </CardHeader>
                </Card>

                <Card className="bg-teal-50 border-teal-200">
                  <CardHeader>
                    <Shield className="h-10 w-10 text-teal-600 mb-3" />
                    <CardTitle>Fully Compliant</CardTitle>
                    <CardDescription className="text-gray-700">
                      All services meet regulatory requirements
                    </CardDescription>
                  </CardHeader>
                </Card>

                <Card className="bg-cyan-50 border-cyan-200">
                  <CardHeader>
                    <CheckCircle2 className="h-10 w-10 text-cyan-600 mb-3" />
                    <CardTitle>24/7 Support</CardTitle>
                    <CardDescription className="text-gray-700">
                      Round-the-clock customer care and assistance
                    </CardDescription>
                  </CardHeader>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Storage Tanks Gallery */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-emerald-100 text-emerald-700 text-base px-4 py-1">Our Infrastructure</Badge>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Diesel Storage Facilities</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional-grade storage tanks built to industry standards
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div className="relative h-80 rounded-2xl overflow-hidden shadow-xl">
                <img src="/images/diesel-tanks-1.png" alt="Industrial Diesel Storage" className="w-full h-full object-cover" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="relative h-40 rounded-xl overflow-hidden shadow-md">
                  <img src="/images/diesel-tanks-2.png" alt="Storage Tank Detail" className="w-full h-full object-cover" />
                </div>
                <div className="relative h-40 rounded-xl overflow-hidden shadow-md">
                  <img src="/images/petroleum-refinery.png" alt="Refinery" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>

            <div className="flex flex-col justify-center space-y-6">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Industry-Leading Storage Solutions</h3>
                <p className="text-lg text-gray-600 mb-6">
                  Our diesel storage facilities are designed and maintained to the highest safety and quality standards, ensuring product integrity from supplier to customer.
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { title: 'Multi-Product Capability', desc: 'Separate tanks for Diesel 50ppm, 10ppm, Petrol, and Jet Fuel' },
                  { title: 'Advanced Monitoring', desc: 'Real-time level monitoring and leak detection systems' },
                  { title: 'Regulatory Compliance', desc: 'Fully compliant with SA Department of Energy standards' },
                  { title: 'Quality Preservation', desc: 'Temperature-controlled environments preserve fuel quality' }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4">
                    <div className="h-10 w-10 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900">{item.title}</h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-12">
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl group">
              <img src="/images/diesel-tanks-3.png" alt="Tank Farm Aerial" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent flex items-end">
                <div className="p-8 text-white max-w-2xl">
                  <h3 className="text-3xl font-bold mb-2">Strategic Tank Farm Network</h3>
                  <p className="text-lg text-emerald-50">
                    Multiple storage facilities strategically located to ensure consistent supply across Southern Africa
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;